import { PaperPlaneRightIcon } from '@phosphor-icons/react';
import { Drawer as AntdDrawer, Input } from 'antd';
import { useEffect, useState } from 'react';

import { Chip, type ChipIntent } from '@/components/common/chip';
import { CloseButton } from '@/components/common/close-button';
import { UserAvatar } from '@/components/common/user-avatar';
import { ErrorState } from '@/components/feedback/error-state';
import { useToast } from '@/components/feedback/toast';

import { MESSAGE_MAX } from '../constants';
import { useMarkTicketRead, useSendMessage, useSupportTicketDetail } from '../hooks';
import { formatMessageTime, groupMessagesByDay } from '../messages';
import type { SupportMessage, SupportTicketDetail, TicketStatus } from '../types';

import styles from './support-ticket-detail-drawer.module.scss';
import { SupportTicketDetailSkeleton } from './support-ticket-detail-skeleton';

export type SupportTicketDetailDrawerProps = {
  open: boolean;
  ticketId: string | null;
  onClose: () => void;
};

const STATUS_CHIP: Record<TicketStatus, { label: string; intent: ChipIntent }> = {
  open: { label: 'Open', intent: 'review' },
  active: { label: 'Active', intent: 'approved' },
  closed: { label: 'Closed', intent: 'neutral' },
  invalid: { label: 'Invalid', intent: 'attention' },
};

/**
 * Ticket Detail chat drawer (Figma 2327-706785). Dark-green header (title, agent, status,
 * #ref), date-separated message bubbles (support left/gray, grower right/yellow) with image
 * thumbnails, and a text composer. Marks the ticket read on open (story 101970 §3).
 */
export function SupportTicketDetailDrawer({
  open,
  ticketId,
  onClose,
}: SupportTicketDetailDrawerProps) {
  const query = useSupportTicketDetail(ticketId ?? '', open && ticketId != null);
  const markRead = useMarkTicketRead();
  const detail = query.data;

  useEffect(() => {
    // Only mark read once the conversation has actually loaded — avoids firing a read
    // against a stale/deleted ticket id that never resolves (story 101970 §3).
    if (open && ticketId && query.isSuccess) markRead.mutate(ticketId);
    // markRead identity is stable; re-run when the opened ticket loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId, query.isSuccess]);

  return (
    <AntdDrawer
      open={open}
      onClose={onClose}
      placement="right"
      width={620}
      closable={false}
      title={null}
      footer={null}
      destroyOnHidden
      classNames={{ body: styles.drawerBody }}
    >
      {query.isError ? (
        <div className={styles.center}>
          <ErrorState
            title="Unable to load ticket"
            description="Please check your internet connection and try again."
            onRetry={() => void query.refetch()}
          />
        </div>
      ) : query.isLoading || !detail ? (
        <SupportTicketDetailSkeleton />
      ) : (
        <DetailBody detail={detail} onClose={onClose} />
      )}
    </AntdDrawer>
  );
}

function DetailBody({ detail, onClose }: { detail: SupportTicketDetail; onClose: () => void }) {
  const toast = useToast();
  const send = useSendMessage(detail.id);
  const [body, setBody] = useState('');

  const canSend = body.trim().length > 0 && !send.isPending;
  const chip = STATUS_CHIP[detail.status];
  const groups = groupMessagesByDay(detail.messages);

  const submit = () => {
    if (!canSend) return;
    send.mutate(
      { body: body.trim() },
      {
        onSuccess: () => setBody(''),
        onError: () =>
          toast.error({ title: "Couldn't send message", description: 'Please try again.' }),
      },
    );
  };

  return (
    <div className={styles.body}>
      <header className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{detail.title}</h2>
            {detail.agent ? (
              <span className={styles.agent}>
                <UserAvatar name={detail.agent.name} size="sm" />
                {detail.agent.name} <span className={styles.role}>({detail.agent.role})</span>
              </span>
            ) : null}
          </div>
          <div className={styles.headerRight}>
            <Chip intent={chip.intent}>{chip.label}</Chip>
            <span className={styles.ref}>#{detail.ref}</span>
          </div>
        </div>
        <CloseButton className={styles.close} onClick={onClose} />
      </header>

      <div className={styles.thread}>
        {groups.map((group) => (
          <div key={group.day} className={styles.dayGroup}>
            <div className={styles.daySeparator}>
              <span>{group.label}</span>
            </div>
            {group.messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
          </div>
        ))}
      </div>

      <div className={styles.composer}>
        <Input.TextArea
          value={body}
          maxLength={MESSAGE_MAX}
          autoSize={{ minRows: 1, maxRows: 4 }}
          placeholder="Type your message here …"
          onChange={(e) => setBody(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          type="button"
          className={styles.send}
          disabled={!canSend}
          aria-label="Send message"
          onClick={submit}
        >
          <PaperPlaneRightIcon size={18} weight="fill" />
        </button>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: SupportMessage }) {
  const mine = message.author === 'grower';
  return (
    <div className={`${styles.bubbleRow} ${mine ? styles.bubbleMine : ''}`}>
      <div className={`${styles.bubble} ${mine ? styles.bubbleSent : styles.bubbleReceived}`}>
        <p className={styles.bubbleText}>{message.body}</p>
        {message.imageUrls.length > 0 ? (
          <div className={styles.thumbs}>
            {message.imageUrls.map((url, i) => (
              <span className={styles.thumb} key={`${message.id}-img-${i}`}>
                <img src={url} alt="" />
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <span className={styles.time}>{formatMessageTime(message.createdAt)}</span>
    </div>
  );
}
